import { whenAu, dayLabel } from './format'
import { monthLabel, businessToday, addDays } from '../../lib/dates'

const SCOPE_NAMES = {
  'accounting.reports.profitandloss.read': 'P&L report',
  'accounting.settings.read': 'chart of accounts',
  'payroll.timesheets.read': 'timesheets',
}

function Chip({ ok, children }) {
  return (
    <span className={[
      'text-xs px-2.5 py-1 rounded-full border inline-flex items-center gap-1.5',
      ok ? 'bg-white border-slate-200 text-slate-700' : 'bg-amber-50 border-amber-300 text-amber-800',
    ].join(' ')}>
      <span className={ok ? 'text-emerald-600' : 'text-amber-500'}>●</span>
      {children}
    </span>
  )
}

/**
 * Data-status chips under the page header. status = GET /status; columns =
 * statement.columns (to flag the open, provisional period).
 */
export default function StatusChips({ status, statusError, columns }) {
  if (statusError) {
    return (
      <div className="flex flex-wrap gap-1.5 mb-3.5">
        <Chip ok={false}>Couldn&apos;t read data status</Chip>
      </div>
    )
  }
  if (!status) return <div className="flex flex-wrap gap-1.5 mb-3.5"><div className="skeleton h-6 w-72 rounded-full" /></div>

  const today = businessToday()
  const yesterday = addDays(today, -1)
  const chips = []

  const computedTo = status.daily_computed_to
  const fresh = computedTo && computedTo >= yesterday
  const at = whenAu(status.last_refresh)
  chips.push(
    <Chip key="portal" ok={!!fresh}>
      {computedTo
        ? `Portal revenue & shipping: to ${dayLabel(computedTo)}${at ? ` (refreshed ${at})` : ''}`
        : 'Portal revenue & shipping: not calculated yet'}
    </Chip>,
  )

  const missing = status.missing_scopes || []
  if (!status.xero_connected) {
    chips.push(<Chip key="xero" ok={false}>Xero not connected: wages and overheads unknown</Chip>)
  } else if (missing.length) {
    const names = missing.map((s) => SCOPE_NAMES[s] || s).join(', ')
    chips.push(<Chip key="xero" ok={false}>Reconnect Xero to grant P&amp;L access ({names})</Chip>)
  }

  if (status.xero_connected) {
    chips.push(
      <Chip key="ts" ok={!!status.timesheets_to}>
        {status.timesheets_to ? `Xero timesheets: to ${dayLabel(status.timesheets_to)}` : 'Xero timesheets: none fetched yet'}
      </Chip>,
    )
  }

  const months = new Set((status.xero_months || []).map((m) => String(m).slice(0, 7)))
  for (const c of columns || []) {
    if (!c?.is_current) continue
    if (c.provisional) {
      chips.push(<Chip key={`prov-${c.key}`} ok={false}>{c.label}: still open, so figures are provisional</Chip>)
    }
    // Overheads for the open column's months: flag any month Xero hasn't been read for.
    const from = c.from ? String(c.from).slice(0, 7) : null
    const to = c.to ? String(c.to).slice(0, 7) : null
    if (from && to && status.xero_connected) {
      const gaps = []
      for (let m = `${from}-01`; m.slice(0, 7) <= to && m <= today; ) {
        if (!months.has(m.slice(0, 7))) gaps.push(monthLabel(m, true))
        const [y, mo] = m.split('-').map(Number)
        m = mo === 12 ? `${y + 1}-01-01` : `${y}-${String(mo + 1).padStart(2, '0')}-01`
      }
      if (gaps.length) {
        chips.push(<Chip key={`xm-${c.key}`} ok={false}>Xero P&amp;L not fetched for {gaps.join(', ')}: overheads unknown</Chip>)
      }
    }
  }

  return <div className="flex flex-wrap gap-1.5 mb-3.5">{chips}</div>
}
